import { Diagnosis } from '../diagnoses/diagnosis';
import { LocalizedFinding } from '../diagnoses/localized-finding';
import { SymptomLike } from '../diagnoses/symptom';

/**
 * A search result for a query.
 */
export class SearchResults {
  public readonly items: SearchResult[];
  public readonly offset: number;
  public readonly limit: number;
  public readonly total: number;

  public readonly micros?: number;

  constructor({
    items,
    offset = 0,
    limit = 0,
    total = 0,
    micros
  }: {
    items: SearchResult[];
    offset?: number;
    limit?: number;
    total?: number;
    micros?: number;
  }) {
    this.items = items;
    this.offset = offset;
    this.limit = limit;
    this.total = total;
    this.micros = micros;
  }

  static fromJson(json: any): SearchResults {
    // Loop over the matching diagnoses and create SearchResult objects for each one
    const items = (json.items || []).map((itemJson: any) => {
      // Get the diagnosis
      const diagnosis = Diagnosis.fromJson(itemJson.diagnosis);
      // Get the matching symptoms
      const symptomHits = (itemJson.symptom_hits || []).map((hitJson: any) => {
        return new SymptomHit({
          symptom: SymptomLike.fromJson(hitJson.symptom),
          score: hitJson.score
        });
      });
      // Get the matching localized findings
      const localizedHits = (itemJson.localized_hits || []).map((hitJson: any) => {
        const localized = LocalizedFinding.fromJson(hitJson.localized);
        return new LocalizedHit({
          localized,
          match: hitJson.match || [],
          score: hitJson.score
        });
      });
      // Create the SearchResult object
      return new SearchResult({
        diagnosis,
        score: itemJson.score,
        symptomHits,
        localizedHits,
        breakdown: itemJson.breakdown || {}
      });
    });

    return new SearchResults({
      items,
      offset: json.offset,
      limit: json.limit,
      total: json.totalResults,
      micros: json.micros
    });
  }
}

export class SearchResult {
  // The diagnosis hit
  public readonly diagnosis: Diagnosis;

  // The score of the diagnosis hit
  public readonly score: number;

  // The matching symptoms and localized findings
  public readonly symptomHits: SymptomHit[];
  public readonly localizedHits: LocalizedHit[];

  // Breakdown of the score
  public readonly breakdown?: { [key: string]: number };

  constructor({
    diagnosis,
    score,
    symptomHits,
    localizedHits,
    breakdown = {}
  }: {
    diagnosis: Diagnosis;
    score: number;
    symptomHits: SymptomHit[];
    localizedHits: LocalizedHit[];
    breakdown?: { [key: string]: number };
  }) {
    this.diagnosis = diagnosis;
    this.score = score;
    this.symptomHits = symptomHits;
    this.localizedHits = localizedHits;
    this.breakdown = breakdown;
  }

  get hits(): number {
    return this.symptomHits.length + this.localizedHits.length;
  }

  get numSymptomHits(): number {
    return this.symptomHits.length;
  }

  get numLocalizedHits(): number {
    return this.localizedHits.length;
  }
}

export class SymptomHit {
  // What the hit was
  public readonly symptom: SymptomLike;

  // The score of the hit
  public readonly score: number;

  constructor({ symptom, score }: { symptom: SymptomLike; score: number }) {
    this.symptom = symptom;
    this.score = score;
  }
}

export class LocalizedHit {
  // What the hit was
  public readonly localized: LocalizedFinding;
  public readonly match: { attribute: string; score: number }[];

  // The score of the hit
  public readonly score: number;

  constructor({
    localized,
    match,
    score
  }: {
    localized: LocalizedFinding;
    match: { attribute: string; score: number }[];
    score: number;
  }) {
    this.localized = localized;
    this.match = match;
    this.score = score;
  }
}
